import { useEffect, useRef, useState } from "react";
import { ChevronDown, LogOut, UserCog, X } from "lucide-react";
import { type AuthUser, updateMyPassword } from "../authClient";

export function AccountMenu({
  user,
  onLogout,
  onUpdateUser,
}: {
  user: AuthUser;
  onLogout: () => void;
  onUpdateUser: (user: AuthUser) => void;
}) {
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [editError, setEditError] = useState("");
  const [saving, setSaving] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  function openEdit() {
    setOpen(false);
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setEditError("");
    setEditOpen(true);
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setEditError("");
    if (newPassword !== confirmPassword) {
      setEditError("两次输入的新密码不一致");
      return;
    }
    setSaving(true);
    try {
      const updated = await updateMyPassword(currentPassword, newPassword);
      onUpdateUser(updated);
      setEditOpen(false);
    } catch (err) {
      setEditError(err instanceof Error ? err.message : "保存失败");
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <div className="account-menu" ref={menuRef}>
        <button className="btn btn-text account-menu-trigger" type="button" onClick={() => setOpen((v) => !v)}>
          <span>{user.username}</span>
          <ChevronDown size={14} className="icon" />
        </button>
        {open && (
          <div className="account-menu-dropdown">
            <button className="account-menu-item" type="button" onClick={openEdit}>
              <UserCog size={14} className="icon" />
              <span>编辑账号</span>
            </button>
            <button
              className="account-menu-item"
              type="button"
              onClick={() => {
                setOpen(false);
                onLogout();
              }}
            >
              <LogOut size={14} className="icon" />
              <span>退出登录</span>
            </button>
          </div>
        )}
      </div>

      {editOpen && (
        <div className="history-modal-overlay" onClick={() => setEditOpen(false)}>
          <div className="history-modal-panel" onClick={(e) => e.stopPropagation()}>
            <div className="history-modal-header">
              <h2 className="history-modal-title">编辑账号</h2>
              <button className="btn btn-text history-modal-close" type="button" onClick={() => setEditOpen(false)}>
                <X size={16} />
              </button>
            </div>
            <form onSubmit={handleSave}>
              <div className="form-group">
                <label className="form-label">用户名</label>
                <p className="account-menu-username">{user.username}</p>
              </div>
              <div className="form-group">
                <label className="form-label">当前密码</label>
                <input
                  className="form-input"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  autoComplete="current-password"
                />
              </div>
              <div className="form-group">
                <label className="form-label">新密码</label>
                <input
                  className="form-input"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  autoComplete="new-password"
                />
                <span className="form-hint">至少 6 位</span>
              </div>
              <div className="form-group">
                <label className="form-label">确认新密码</label>
                <input
                  className="form-input"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                />
              </div>
              {editError && <p className="inline-hint">{editError}</p>}
              <div className="history-modal-actions">
                <button className="btn btn-text" type="button" onClick={() => setEditOpen(false)}>
                  取消
                </button>
                <button
                  className="btn btn-clay"
                  type="submit"
                  disabled={saving || !currentPassword || newPassword.length < 6 || !confirmPassword}
                >
                  保存
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
